/**
 * Turns the Dayjs a `date` or `dateRange` DatePicker holds into the `Timestamp`
 * the cell stores: start of the picked day in `defaultTimeZone`. Consumers:
 * `ControlUI`'s `date` and `dateRange` writes, `WorkflowParameterContent`'s
 * `handleOk`, and `timestampFromDateString` for a date-only column.
 *
 * Reads only the Dayjs's calendar fields, so the browser zone never enters and
 * every server reader, all of which format in `defaultTimeZone`, sees the day
 * that was picked (SYD-1785, SYD-2507). `dateOnlyDayjs` is the inverse for
 * display. `dateTime`, `time` and their ranges are instants and keep
 * `Timestamp.fromDate`. Cells a Central browser wrote before SYD-2507 already
 * sit on this anchor; cells other browsers wrote are read as-is, no migration.
 */
import type { Dayjs } from 'dayjs';
import { Timestamp } from 'firebase/firestore';
import { DateTime } from 'luxon';
import { defaultTimeZone } from '#types/Client.Parameters.types';

export const dateOnlyTimestamp = (picked: Dayjs): Timestamp =>
  Timestamp.fromMillis(
    DateTime.fromObject(
      { year: picked.year(), month: picked.month() + 1, day: picked.date() },
      { zone: defaultTimeZone },
    ).toMillis(),
  );
